// 床の技の予告の見えやすさ (2026-11-01〜)。作者 11-01「全てのボスに共通して、床技系全てに言えることなんだけど、視認性が悪い」。
//   避けること (11-01 作者): 全ボスに同じ「床の動き」を足す (「アニメーションはきれいなんだけど、それで視認性が上がるわけではないから却下／
//     加えて、床が盛り上がるのが正しい技が全てではない」)。技ごとに「何が起きる技か」の形で見せる。
//   配布版の既定 (11-02 作者 crack=ok): 騎士の石突と介錯の地割りに、白い粉の詰まった割れ目と跳ねる粒 (割れ目は手前の足より下にも描き直す)。
//   花嫁の床の技は今のまま (11-02 作者「花嫁の床技は視認性良いから変えなくて良い」)。今の見せ方を他の技の手本にする:
//     色 = 技そのものの濃い色を、色味の薄い床に置く (明るさでなく色合いで浮く)。
//     形 = 本番の技の小さな先触れ (茨の先がもう床から出ている)。床の線より上へ縦に立つ所がある。
//     前後 = キャラの後ろでよい。足より広く、縦に立つので足に隠れない。
//     出だし = 最初のコマから見える濃さで出て、そこから育つ (端から広げない)。
//   その手本で作り直して配布版の既定 (11-02 作者 model=ok・hem=ok): 剣舞の裾払い = 刃の花の先と菫の溜まり。
//   介錯の榴弾は配布版の床の目印を今のまま (11-02 作者「介錯の2つは両方やめてほしいって意味」)。避けること: 着弾点の赤い火・
//     上から降る火の粉を配布版へ入れる。比較版として DEV ?floorVis=d1 (着弾点の赤い火の舌)・d2 (上から降る火の粉) に残す。
//   前の版 (DEV): ?floorVis=crack = 割れ目だけ (11-02 昼の配布版)・?floorVis=off = 予告に何も足さない。
//   鶏の羽根は別の比較版 ?roosterFx=v1 があるので触らない。
import type { ActiveHit, GameState } from './types'
import { ARENA } from './balance'
import { crackReady, crackSprite, drawMat, dustReady, dustSprite, mistReady, mistSprite, MIST_FORMS, MIST_STEPS, preloadExecFxMats, tinted } from './exec_fx_mats'
import { drawBladeHemTipsPreview } from './blade_hem_bloom'

/** 'ship' = 配布版・'crack' = 割れ目だけの前の版・'off' = 何も足さない・'d1' / 'd2' = 榴弾の比較版 (配布版に入れない)。 */
export type FloorWarnMode = 'ship' | 'off' | 'crack' | 'd1' | 'd2'
let mode: FloorWarnMode = 'ship'

/** game.ts が DEV の時だけ呼ぶ (?floorVis=)。知らない値は配布版。 */
export function setFloorWarnMode(raw: string | null): void {
  mode = raw === 'off' || raw === 'crack' || raw === 'd1' || raw === 'd2' ? raw : 'ship'
}
export function floorWarnMode(): FloorWarnMode { return mode }

const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
const smooth = (e0: number, e1: number, x: number): number => {
  const u = clamp01((x - e0) / (e1 - e0))
  return u * u * (3 - 2 * u)
}
// 帯ごとに決まった揺らぎ (同じ帯は毎コマ同じ形)
const hash = (n: number): number => {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return s - Math.floor(s)
}

/** 予告中の床の帯か (体に付いた当たり・空気の波は外す)。 */
function isFloorTelegraph(hz: ActiveHit): boolean {
  return !hz.follow && !hz.airWave && hz.xEnd - hz.xStart > 0 && hz.elapsedMs < hz.telegraphMs
}

/** 技ごとの見せ方。null = 何もしない技 (花嫁の床の技はここに入れない)。 */
type Look = 'crack' | 'quake' | 'hemTips' | 'shellTongue' | 'shellEmber'
export function lookOf(hz: Pick<ActiveHit, 'kind' | 'attackId'>): Look | null {
  if (mode === 'off') return null
  if (hz.kind === 'buttTrap') return 'crack'                                       // 騎士 石突: 床が下から突き破られる
  if (hz.attackId === 'exec_maulQuake') return 'quake'                             // 介錯 地割り: 割れ目が外へ走る
  if (mode === 'crack') return null
  if (hz.attackId === 'hemSweep') return 'hemTips'                                 // 剣舞 裾払い: 刃の花の先がもう床から出ている
  if (hz.attackId === 'exec_cannonMortar') {                                      // 介錯 榴弾: 配布版は足さない (比較版だけ)
    if (mode === 'd1') return 'shellTongue'
    if (mode === 'd2') return 'shellEmber'
  }
  return null
}
// 花嫁を手本にした見せ方は、キャラの後ろだけに描く (縦に立つので足に隠れない・手前の描き直しはしない)。
const BACK_ONLY: ReadonlySet<Look> = new Set<Look>(['hemTips', 'shellTongue', 'shellEmber'])

// 床の帯の上下 (足の線の少し奥から、少し手前まで。床は奥行きのある面なので、薄い線にしない)
const TOP = -9
const BOT = 15
const FRONT_Y = 3          // 床の線からこれより下はキャラの足より手前 (手前の描き直しはここから下だけ)
const PALE_STONE = '214,206,196'   // 割れ目の中の石の粉 (灰の白・常夜燈の青や血の赤と混ざらない)
const DARK_LIP = '18,12,14'
const GRIT = '206,198,188'

type Pt = readonly [number, number]
function fillPoly(ctx: CanvasRenderingContext2D, pts: readonly Pt[]): void {
  ctx.beginPath()
  pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)))
  ctx.closePath()
}

/** 広がりの先が、ぎざぎざの縦の縁になる切り抜き (fromLeft = 左から広がる・u = 0..1)。 */
function revealClip(ctx: CanvasRenderingContext2D, x0: number, x1: number, fy: number, fromLeft: boolean, u: number, seed: number): void {
  const w = x1 - x0, y0 = fy + TOP - 30, y1 = fy + BOT + 30
  const front = fromLeft ? x0 + w * u : x1 - w * u
  const pts: Pt[] = []
  const steps = 8
  for (let k = 0; k <= steps; k++) {
    const y = y0 + ((y1 - y0) * k) / steps
    pts.push([front + (hash(seed + k * 1.9) - 0.5) * 12 * (fromLeft ? 1 : -1), y])
  }
  const back = fromLeft ? x0 - 40 : x1 + 40
  fillPoly(ctx, [[back, y0], ...pts, [back, y1]])
  ctx.clip()
}

/** 割れ目の絵 (塗り直した写し) を帯の幅に並べる。下に1px ずらした濃い縁を置いて、彫り込んだ形にする。 */
function drawCracks(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, h: number, alpha: number, seed: number): void {
  if (!crackReady()) return
  const w = x1 - x0
  const n = Math.max(1, Math.round(w / 110))
  for (let k = 0; k < n; k++) {
    const img = crackSprite(Math.floor(hash(seed + k * 4.1) * 4))
    if (!img) continue
    const cx = x0 + (w * (k + 0.5)) / n
    const cw = (w / n) * 1.15
    const yy = y + (hash(seed + k * 7.3) - 0.5) * 6
    const flip = hash(seed + k * 2.7) > 0.5
    drawMat(ctx, tinted(img, DARK_LIP), cx, yy + 2, cw, h, 0.5, 0.5, alpha * 0.85, 0, flip)
    drawMat(ctx, tinted(img, PALE_STONE), cx, yy, cw, h, 0.5, 0.5, alpha, 0, flip)
  }
}

/** 灰白の粒が跳ねる (source-over)。xs = 跳ねる所・rate = 1秒に跳ねる回数・maxH = 跳ねる高さ。 */
function drawGrit(ctx: CanvasRenderingContext2D, xs: readonly number[], baseY: number, t: number, u: number, rate: number, maxH: number, size: number, alpha: number, seed: number): void {
  if (!dustReady() || u <= 0.01) return
  xs.forEach((x, k) => {
    const ph = (t * rate + hash(seed + k * 5.3)) % 1
    const hop = Math.sin(Math.PI * ph) * maxH * (0.6 + 0.4 * hash(seed + k))
    const img = dustSprite('bit', k)
    if (!img) return
    const sz = size * (0.75 + 0.5 * hash(seed + k * 3.7))
    drawMat(ctx, tinted(img, GRIT), x, baseY - hop, sz, sz, 0.5, 0.9, alpha * u, hash(k * 9.1) * 6)
  })
}

/** 床に置く色の溜まり (横長の楕円・縁はぼかす・光らせない普通の重ね)。 */
function floorPool(ctx: CanvasRenderingContext2D, cx: number, fy: number, rx: number, ry: number, rgb: string, alpha: number): void {
  if (alpha <= 0.01 || rx <= 1) return
  ctx.save()
  ctx.translate(cx, fy)
  ctx.scale(rx / ry, 1)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, ry)
  g.addColorStop(0, `rgba(${rgb},${alpha.toFixed(3)})`)
  g.addColorStop(0.6, `rgba(${rgb},${(alpha * 0.7).toFixed(3)})`)
  g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(0, 0, ry, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

// 介錯の火の色 (本番の爆炎と同じ赤・外の暗い赤と芯の明るい赤)
const FIRE_DEEP = '150,18,16'
const FIRE_CORE = '236,72,40'

/** 着弾点の床から立つ火の小さな舌 (案①)。靄の絵を火の色で塗り、根元を床に置いて縦に立てる。u = 育ち 0..1。 */
function drawFireTongues(ctx: CanvasRenderingContext2D, cx: number, w: number, fy: number, t: number, u: number, seed: number): void {
  floorPool(ctx, cx, fy + 2, w * 0.55, 8, FIRE_DEEP, 0.6 + 0.3 * u)
  if (!mistReady()) return
  const n = Math.max(3, Math.min(5, Math.round(w / 22)))
  for (let k = 0; k < n; k++) {
    const x = cx + (k - (n - 1) / 2) * (w * 0.7 / Math.max(1, n - 1)) + (hash(seed + k * 3.3) - 0.5) * 6
    const flick = 0.82 + 0.18 * Math.sin(t * (17 + 5 * hash(seed + k)) + k * 2.1)
    const h = (34 + 34 * u) * (0.75 + 0.4 * hash(seed + k * 5.9)) * flick
    const step = Math.floor(t * 12 + k * 1.7) % MIST_STEPS
    const img = mistSprite(Math.floor(hash(seed + k * 7.7) * MIST_FORMS), step)
    if (!img) continue
    const ww = h * 0.7
    drawMat(ctx, tinted(img, FIRE_DEEP), x, fy + 3, ww * 1.25, h * 1.15, 0.5, 1, 1, 0, k % 2 === 1)
    drawMat(ctx, tinted(img, FIRE_CORE), x, fy + 3, ww * 0.85, h * 0.85, 0.5, 1, 1, 0, k % 2 === 0)
    drawMat(ctx, tinted(img, FIRE_CORE), x, fy + 3, ww * 0.5, h * 0.55, 0.5, 1, 1, 0, k % 2 === 1)
  }
}

/** 上から降る赤い火の粉の筋 (案②)。着弾点の真上から床へ、細い筋になって降り続ける。u = 育ち 0..1 (近づくほど濃く速く)。 */
function drawEmberFall(ctx: CanvasRenderingContext2D, cx: number, w: number, fy: number, t: number, u: number, seed: number): void {
  floorPool(ctx, cx, fy + 2, w * 0.5, 8, FIRE_DEEP, 0.55 + 0.3 * u)
  if (!dustReady()) return
  const top = fy - 300
  const n = 22
  const rate = 0.9 + 0.9 * u
  for (let k = 0; k < n; k++) {
    const ph = (t * rate * (0.85 + 0.3 * hash(seed + k)) + hash(seed + k * 4.3)) % 1
    const y = top + (fy - top) * ph * ph
    const x = cx + (hash(seed + k * 2.1) - 0.5) * w * 0.45 + Math.sin(t * 3 + k) * 3
    const img = dustSprite('bit', k)
    if (!img) continue
    const sz = 11 + 6 * hash(seed + k * 6.1)
    // 落ちるほど速いので縦に伸ばす。床の近くで消える前に一度明るくなる
    const stretch = 1.4 + 2.2 * ph
    const a = (0.8 + 0.2 * u) * Math.min(1, ph * 6) * (ph > 0.94 ? (1 - ph) / 0.06 : 1)
    drawMat(ctx, tinted(img, FIRE_DEEP), x, y, sz * 1.3, sz * stretch * 1.3, 0.5, 0.8, a)
    drawMat(ctx, tinted(img, FIRE_CORE), x, y, sz * 0.8, sz * stretch * 0.8, 0.5, 0.8, a)
  }
  // 床に落ちた火の粉が小さく跳ねる
  const xs = [cx - w * 0.18, cx + w * 0.12, cx - w * 0.02]
  xs.forEach((x, k) => {
    const ph = (t * 2.4 + hash(seed + 30 + k)) % 1
    const img = dustSprite('bit', k + 2)
    if (img) drawMat(ctx, tinted(img, FIRE_CORE), x, fy + 2 - Math.sin(Math.PI * ph) * 12, 11, 11, 0.5, 0.9, 0.8 * (0.5 + 0.5 * u))
  })
}

function drawLook(ctx: CanvasRenderingContext2D, hz: ActiveHit, look: Look, t: number, bossX: number | null, front: boolean): void {
  const p = hz.elapsedMs / hz.telegraphMs
  const x0 = hz.xStart, x1 = hz.xEnd, w = x1 - x0, cx = (x0 + x1) / 2
  const fy = ARENA.floorY
  const seed = Math.round(x0) * 0.37 + Math.round(x1) * 0.11
  // 広がる向き = ボスに近い側から外へ (ボスが分からない時は左から)
  const fromLeft = bossX === null ? true : Math.abs(bossX - x0) < Math.abs(bossX - x1)
  switch (look) {
    case 'crack': {
      // 突き破られる床: 白い粉の詰まった割れ目が帯の端まで走り (40% まで)、割れ目から粒が跳ね始める
      const u = smooth(0.0, 0.4, p)
      if (!front) {
        ctx.save(); revealClip(ctx, x0, x1, fy, fromLeft, u, seed)
        drawCracks(ctx, x0, x1, fy + 4, 16, 0.95, seed)
        ctx.restore()
      } else {
        const xs = Array.from({ length: Math.max(4, Math.round(w / 16)) }, (_, k) => x0 + 6 + (w - 12) * hash(seed + k * 1.3))
        drawGrit(ctx, xs, fy + 8, t, smooth(0.2, 0.55, p), 2.6 + 3 * p, 6 + 10 * p, 13, 0.95, seed)
      }
      return
    }
    case 'quake': {
      // 割れ目が内から外へ走る (帯ごとに予告の 10〜50% で端まで)・走る先で石が跳ねる
      const u = smooth(0.08, 0.5, p)
      const out = bossX === null ? true : cx > bossX
      if (!front) {
        ctx.save(); revealClip(ctx, x0, x1, fy, out, u, seed)
        drawCracks(ctx, x0 - 6, x1 + 6, fy + 4, 15, 0.95, seed)
        ctx.restore()
      } else {
        const tip = out ? x0 + w * u : x1 - w * u
        drawGrit(ctx, [tip, tip + (out ? -10 : 10)], fy + 8, t, u < 0.99 ? u : smooth(0.5, 0.7, p), 4, 12, 13, 0.95, seed)
      }
      return
    }
    case 'hemTips': {
      // 花嫁の茨の先と同じ考え: 当たりの刃の花の先が、最初のコマから床に並んで少し出ている (育って高くなる)。
      //   金の板の床に、刃と同じ菫の溜まりを敷く (明るさでなく色合いで浮く)。
      const on = smooth(0, 0.06, p)
      floorPool(ctx, cx, fy + 3, w / 2 + 6, 9, '112,64,184', (0.5 + 0.15 * p) * on)
      drawBladeHemTipsPreview(ctx, hz, t, fy, bossX ?? cx, 0.16 + 0.14 * smooth(0, 1, p), 0.95 * on)
      return
    }
    case 'shellTongue':
      drawFireTongues(ctx, cx, w, fy, t, smooth(0, 1, p) * smooth(0, 0.06, p), seed)
      return
    case 'shellEmber':
      drawEmberFall(ctx, cx, w, fy, t, smooth(0, 1, p), seed)
      return
  }
}

let preloaded = false

/** キャラの後ろ・床の上 (drawHazards の帯ごとに、各ボスの予告より先に呼ぶ)。 */
export function drawFloorWarnUnder(ctx: CanvasRenderingContext2D, hz: ActiveHit, t: number, bossX: number | null = null): void {
  if (!isFloorTelegraph(hz)) return
  const look = lookOf(hz)
  if (look) drawLook(ctx, hz, look, t, bossX, false)
}

/** キャラの手前: 床の線より下 (足より手前) の分の描き直しと、跳ねる粒。 */
export function drawFloorWarnFront(ctx: CanvasRenderingContext2D, state: GameState): void {
  if (state.phase !== 'playing') return
  // 割れ目と粒の絵 (介錯の素材・約 1.1MB) を、戦いが始まった所で1回だけ読み始める
  if (!preloaded && mode !== 'off') { preloaded = true; preloadExecFxMats() }
  const fy = ARENA.floorY
  const bossX = state.boss ? state.boss.x : null
  for (const hz of state.hits) {
    if (!isFloorTelegraph(hz)) continue
    const look = lookOf(hz)
    if (!look || BACK_ONLY.has(look)) continue
    // 床に敷いた物 (割れ目) の手前の縁を、足より下だけ描き直す
    ctx.save()
    ctx.beginPath(); ctx.rect(hz.xStart - 60, fy + FRONT_Y, hz.xEnd - hz.xStart + 120, 60); ctx.clip()
    drawLook(ctx, hz, look, state.time, bossX, false)
    ctx.restore()
    drawLook(ctx, hz, look, state.time, bossX, true)
  }
}
